/**
 * TD product compare: sessionStorage store, drawer, card toggles, compare page UI.
 */
(function () {
  'use strict';

  if (window.__TD_PRODUCT_COMPARE_BOOT__) return;
  window.__TD_PRODUCT_COMPARE_BOOT__ = true;

  const STORAGE_KEY = 'td-product-compare';
  const MAX_ITEMS = 3;
  const EVENT_NAME = 'td-product-compare:updated';
  const COMPARE_DESKTOP_BREAKPOINT = 1024;
  const COMPARE_REVIEW_SECTION_ID = 'td-product-compare-data';
  const reviewDataCache = new Map();

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  function isComparePage() {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'compare' || !!document.querySelector('td-product-comparison');
  }

  function readStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeStorage(items) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota */
    }
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw.handle !== 'string' || !raw.handle) return null;
    return {
      handle: raw.handle,
      title: raw.title || raw.handle,
      url: raw.url || '',
      image: raw.image || '',
      imageAspectRatio: raw.imageAspectRatio || '',
    };
  }

  function dispatch(items) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: { items: items.slice() },
        bubbles: true,
      })
    );
  }

  const store = {
    getItems() {
      return readStorage();
    },

    setItems(items) {
      const next = items.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);
      writeStorage(next);
      dispatch(next);
      return next;
    },

    add(raw) {
      const item = normalizeItem(raw);
      if (!item) return this.getItems();
      let items = this.getItems();
      if (items.some((i) => i.handle === item.handle)) return items;
      if (items.length >= MAX_ITEMS) return items;
      items = items.concat([item]);
      return this.setItems(items);
    },

    remove(handle) {
      if (!handle) return this.getItems();
      const items = this.getItems().filter((i) => i.handle !== handle);
      return this.setItems(items);
    },

    clear() {
      return this.setItems([]);
    },

    has(handle) {
      return this.getItems().some((i) => i.handle === handle);
    },

    isFull() {
      return this.getItems().length >= MAX_ITEMS;
    },

    buildCompareUrl(shopUrl) {
      const items = this.getItems();
      if (items.length < 2) return '';
      const primary = items[0].handle;
      const rest = items
        .slice(1)
        .map((i) => i.handle)
        .filter(Boolean);
      const base = shopUrl || '';
      const path = `/products/${encodeURIComponent(primary)}?view=compare`;
      if (rest.length === 0) return `${base}${path}`;
      return `${base}${path}&compare=${rest.map(encodeURIComponent).join('%2C')}`;
    },
  };

  window.TDProductCompareStore = store;

  function onUpdated(cb) {
    const fn = (e) => cb(e.detail?.items || store.getItems());
    document.addEventListener(EVENT_NAME, fn);
    return () => document.removeEventListener(EVENT_NAME, fn);
  }

  window.TDProductCompareOnUpdated = onUpdated;

  function syncCardRoots(items) {
    const handles = new Set(items.map((i) => i.handle));
    document.querySelectorAll('[data-td-compare-card]').forEach((root) => {
      const handle = root.getAttribute('data-td-product-handle');
      if (!handle) return;
      const selected = handles.has(handle);
      const full = items.length >= MAX_ITEMS && !selected;
      root.classList.toggle('td-product-comparison--selected', selected);
      root.classList.toggle('td-product-comparison--disabled', full);
      const input = root.querySelector('[data-td-compare-checkbox]');
      if (input) {
        input.checked = selected;
        input.disabled = full;
        input.setAttribute('aria-disabled', full ? 'true' : 'false');
      }
      const btn = root.querySelector('[data-td-compare-toggle]');
      if (btn) {
        btn.disabled = full;
        btn.setAttribute('aria-disabled', full ? 'true' : 'false');
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.classList.toggle('td-product-comparison--selected', selected);
      }
    });
  }

  document.addEventListener(EVENT_NAME, (e) => {
    syncCardRoots(e.detail?.items || store.getItems());
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-td-compare-toggle]');
    if (!btn || btn.disabled) return;
    const root = btn.closest('[data-td-compare-card]');
    if (!root) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = root.getAttribute('data-td-product-handle');
    if (!handle) return;
    if (store.has(handle)) {
      store.remove(handle);
      return;
    }
    if (store.isFull()) return;
    store.add({
      handle,
      title: root.getAttribute('data-td-product-title') || '',
      url: root.getAttribute('data-td-product-url') || '',
      image: root.getAttribute('data-td-product-image') || '',
      imageAspectRatio: root.getAttribute('data-td-product-aspect') || '',
    });
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-td-compare-checkbox]');
    if (!input || input.disabled) return;
    const root = input.closest('[data-td-compare-card]');
    if (!root) return;
    e.stopPropagation();
    const handle = root.getAttribute('data-td-product-handle');
    if (!handle) return;
    if (input.checked) {
      if (store.isFull() && !store.has(handle)) {
        input.checked = false;
        return;
      }
      const countBefore = store.getItems().length;
      store.add({
        handle,
        title: root.getAttribute('data-td-product-title') || '',
        url: root.getAttribute('data-td-product-url') || '',
        image: root.getAttribute('data-td-product-image') || '',
        imageAspectRatio: root.getAttribute('data-td-product-aspect') || '',
      });
      if (store.getItems().length > countBefore) {
        document.querySelector('td-product-compare-drawer')?.openDrawer?.();
      }
    } else {
      store.remove(handle);
    }
  });

  syncCardRoots(store.getItems());

  class TDProductCompareDrawer extends HTMLElement {
    static SELECTORS = {
      toggle: '[data-td-compare-drawer-toggle]',
      compareBtn: '[data-td-compare-drawer-compare]',
      clearBtn: '[data-td-compare-drawer-clear]',
      slot: '[data-td-compare-slot]',
      count: '[data-td-compare-count]',
    };

    constructor() {
      super();
      this._off = null;
      this._shopUrl = '';
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this._shopUrl = this.getAttribute('data-td-shop-url') || '';
      this._isComparePage = isComparePage();
      this._off = onUpdated((items) => this._render(items));
      this._bind();
      this._render(store.getItems());
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      if (typeof this._off === 'function') this._off();
      document.removeEventListener('keydown', this._onKeydown);
    }

    _onKeydown(e) {
      if (e.key !== 'Escape') return;
      if (this.getAttribute('data-td-open') !== 'true') return;
      this._setOpen(false);
      this.querySelector(TDProductCompareDrawer.SELECTORS.toggle)?.focus();
    }

    _bind() {
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-td-compare-drawer-toggle]');
        if (t) {
          e.preventDefault();
          const open = this.getAttribute('data-td-open') !== 'true';
          this._setOpen(open);
          return;
        }
        const rm = e.target.closest('[data-td-compare-remove]');
        if (rm) {
          e.preventDefault();
          const h = rm.getAttribute('data-td-handle');
          if (h) store.remove(h);
          return;
        }
        const clear = e.target.closest('[data-td-compare-drawer-clear]');
        if (clear) {
          e.preventDefault();
          store.clear();
          return;
        }
        const cmp = e.target.closest('[data-td-compare-drawer-compare]');
        if (cmp && cmp.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
        }
      });
    }

    _setOpen(open) {
      this.setAttribute('data-td-open', open ? 'true' : 'false');
      const btn = this.querySelector(TDProductCompareDrawer.SELECTORS.toggle);
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /** Opens the drawer when it has items (e.g. after checkbox add). */
    openDrawer() {
      if (this.hidden || this._isComparePage) return;
      this._setOpen(true);
    }

    _render(items) {
      if (this._isComparePage) {
        this.hidden = true;
        this._setOpen(false);
        return;
      }

      const empty = items.length === 0;
      this.hidden = empty;
      if (empty) {
        this._setOpen(false);
        return;
      }

      const countEl = this.querySelector(TDProductCompareDrawer.SELECTORS.count);
      if (countEl) {
        const one = this.getAttribute('data-td-count-one') || '1 of 3 items selected';
        const otherTemplate =
          this.getAttribute('data-td-count-other') || '{{ count }} of 3 items selected';
        countEl.textContent =
          items.length === 1
            ? one
            : otherTemplate
                .replace('** count **', String(items.length))
                .replace('{{ count }}', String(items.length));
      }

      const compareBtn = this.querySelector(TDProductCompareDrawer.SELECTORS.compareBtn);
      if (compareBtn) {
        const ok = items.length >= 2;
        const url = store.buildCompareUrl(this._shopUrl);
        const compareLabel = this.getAttribute('data-td-compare-label') || 'Compare';
        compareBtn.setAttribute('href', ok && url ? url : '#');
        compareBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        compareBtn.setAttribute('tabindex', ok ? '0' : '-1');
        compareBtn.classList.toggle('td-product-compare-drawer__compare--disabled', !ok);
        compareBtn.textContent = `${compareLabel} (${items.length})`;
      }

      this.querySelectorAll(TDProductCompareDrawer.SELECTORS.slot).forEach((slot, index) => {
        const item = items[index];
        const placeholderText =
          this.getAttribute(
            ['data-td-placeholder-first', 'data-td-placeholder-second', 'data-td-placeholder-third'][
              index
            ]
          ) || 'Select product to compare';

        if (!item) {
          slot.hidden = false;
          slot.innerHTML = `
            <div class="td-product-compare-drawer__slot-inner td-product-compare-drawer__slot-inner--placeholder">
              <p class="td-product-compare-drawer__slot-placeholder">${escapeHtml(placeholderText)}</p>
            </div>
          `;
          return;
        }
        slot.hidden = false;
        const img = item.image
          ? `<img class="td-product-compare-drawer__thumb-img" src="${escapeAttr(item.image)}" alt="" width="56" height="56" loading="lazy">`
          : '<span class="td-product-compare-drawer__thumb-placeholder"></span>';
        const removeLabel = escapeAttr(this.getAttribute('data-td-remove-label') || 'Remove');
        slot.innerHTML = `
          <div class="td-product-compare-drawer__slot-inner">
            <div class="td-product-compare-drawer__thumb">${img}</div>
            <p class="td-product-compare-drawer__slot-title">${escapeHtml(item.title)}</p>
            <button type="button" class="td-product-compare-drawer__remove button button-unstyled" data-td-compare-remove data-td-handle="${escapeAttr(item.handle)}" aria-label="${removeLabel}">
              <span aria-hidden="true">×</span>
            </button>
          </div>
        `;
      });
    }
  }

  if (!customElements.get('td-product-compare-drawer')) {
    customElements.define('td-product-compare-drawer', TDProductCompareDrawer);
  }

  function routesRootFromDom() {
    const el = document.querySelector('[data-td-routes-root]');
    return el?.getAttribute('data-td-routes-root') || '/';
  }

  function buildComparePageUrl(shopUrl, primary, otherHandles) {
    const path = `/products/${encodeURIComponent(primary)}?view=compare`;
    if (!otherHandles.length) return `${shopUrl}${path}`;
    return `${shopUrl}${path}&compare=${otherHandles.map(encodeURIComponent).join('%2C')}`;
  }

  function parseComparePageHandles(primaryHandle) {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('compare') || '';
    const extra = raw
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    const list = [primaryHandle, ...extra].filter(Boolean);
    const uniq = [];
    for (const h of list) {
      if (!uniq.includes(h)) uniq.push(h);
    }
    return uniq.slice(0, MAX_ITEMS);
  }

  function productImageUrl(p) {
    if (!p) return '';
    if (p.featured_image) {
      const fi = p.featured_image;
      if (typeof fi === 'string') return fi;
      return fi.src || '';
    }
    const img0 = p.images && p.images[0];
    if (!img0) return '';
    if (typeof img0 === 'string') return img0;
    return img0.src || '';
  }

  function formatProductPrice(p) {
    if (!p) return '';
    const { min, max } = getPriceRange(p);
    if (!Number.isFinite(min)) return '';
    if (!Number.isFinite(max) || min === max) return formatMoneyCents(min);
    return `${formatMoneyCents(min)} - ${formatMoneyCents(max)}`;
  }

  function formatCompareAt(p) {
    if (!p) return '';
    const { min, max } = getCompareAtRange(p);
    if (!Number.isFinite(min)) return '';
    if (!Number.isFinite(max) || min === max) return formatMoneyCents(min);
    return `${formatMoneyCents(min)} - ${formatMoneyCents(max)}`;
  }

  function formatMoneyCents(value) {
    if (!Number.isFinite(value)) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    const language = document.documentElement.lang || undefined;

    try {
      return new Intl.NumberFormat(language, {
        style: 'currency',
        currency,
      }).format(value / 100);
    } catch {
      return `$${(value / 100).toFixed(2)}`;
    }
  }

  function getVariantNumbers(product, key) {
    if (!Array.isArray(product?.variants) || product.variants.length === 0) return [];
    return product.variants
      .map((variant) => Number(variant?.[key]))
      .filter((value) => Number.isFinite(value) && value > 0);
  }

  function getPriceRange(product) {
    const values = getVariantNumbers(product, 'price');
    if (values.length === 0) {
      const fallback = Number(product?.price);
      return {
        min: Number.isFinite(fallback) ? fallback : NaN,
        max: Number.isFinite(fallback) ? fallback : NaN,
      };
    }

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }

  function getCompareAtRange(product) {
    const values = getVariantNumbers(product, 'compare_at_price');
    if (values.length === 0) {
      const fallback = Number(product?.compare_at_price);
      return {
        min: Number.isFinite(fallback) ? fallback : NaN,
        max: Number.isFinite(fallback) ? fallback : NaN,
      };
    }

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }

  function getMaxDiscountPercentage(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    let maxDiscount = 0;

    variants.forEach((variant) => {
      const price = Number(variant?.price);
      const compareAt = Number(variant?.compare_at_price);
      if (!Number.isFinite(price) || !Number.isFinite(compareAt) || compareAt <= price || compareAt <= 0) return;
      const discount = Math.round(((compareAt - price) / compareAt) * 100);
      if (discount > maxDiscount) maxDiscount = discount;
    });

    return maxDiscount;
  }

  function renderComparePrice(product) {
    const price = formatProductPrice(product);
    if (!price) return '';

    const compareAt = formatCompareAt(product);
    const showCompareAt = compareAt && getMaxDiscountPercentage(product) > 0;

    return `<div class="td-product-comparison__head-price paragraph-small">
      ${showCompareAt ? `<span class="td-product-comparison__head-price-compare">${escapeHtml(compareAt)}</span>` : ''}
      <span class="td-product-comparison__head-price-current">${escapeHtml(price)}</span>
    </div>`;
  }

  function renderCompareBadge(root, product) {
    const percentage = getMaxDiscountPercentage(product);
    if (!percentage) return '';

    const template = root.getAttribute('data-td-label-save-up-to') || 'Save up to __percentage__%';
    const colorClass = root.getAttribute('data-td-sale-badge-class') || '';
    return `<span class="td-product-comparison__head-badge product-badges__badge product-badges__badge--rectangle ${escapeAttr(colorClass)}">${escapeHtml(
      template.replace('__percentage__', String(percentage))
    )}</span>`;
  }

  function stripHtml(html, maxLen) {
    if (!html) return '';
    const t = String(html)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!maxLen || t.length <= maxLen) return t;
    return `${t.slice(0, maxLen)}…`;
  }

  function productsToStoreItems(products, shopUrl) {
    return products.map((p) => ({
      handle: p.handle,
      title: p.title || p.handle,
      url: `${shopUrl}/products/${encodeURIComponent(p.handle)}`,
      image: productImageUrl(p),
      imageAspectRatio: '',
    }));
  }

  function productJsonUrl(handle, localeRoot) {
    let base = (localeRoot || '').trim();
    if (base === '/' || base === '') {
      base = '';
    } else {
      base = base.replace(/\/+$/, '');
    }
    const path = `${base}/products/${encodeURIComponent(handle)}.js`;
    return path.replace(/\/{2,}/g, '/');
  }

  function fetchProductsJson(handles, localeRoot) {
    return Promise.all(
      handles.map((h) =>
        fetch(productJsonUrl(h, localeRoot))
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
  }

  function hasReviewData(product) {
    const reviews = product?.metafields?.reviews;
    const ratingSource = reviews?.rating?.value || reviews?.rating;
    const reviewCount = reviews?.rating_count || product?.rating_count;
    return ratingSource != null || reviewCount != null;
  }

  function compareReviewSectionUrl(handle, localeRoot) {
    let base = (localeRoot || '').trim();
    if (base === '/' || base === '') {
      base = '';
    } else {
      base = base.replace(/\/+$/, '');
    }
    const path = `${base}/products/${encodeURIComponent(handle)}?section_id=${COMPARE_REVIEW_SECTION_ID}`;
    return path.replace(/\/{2,}/g, '/');
  }

  function parseReviewDataFromSectionMarkup(markup) {
    if (!markup) return null;

    const match = String(markup).match(
      /<script[^>]*data-td-compare-review-data[^>]*>([\s\S]*?)<\/script>/i
    );
    const raw = match?.[1]?.trim();
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      const rating = Number(parsed?.rating);
      const scaleMax = Number(parsed?.scaleMax) || 5;
      const ratingCount = Number(parsed?.ratingCount);

      if (!Number.isFinite(rating) || rating <= 0) return null;

      return {
        rating,
        scaleMax,
        ratingCount: Number.isFinite(ratingCount) && ratingCount >= 0 ? ratingCount : null,
      };
    } catch {
      return null;
    }
  }

  async function fetchReviewData(handle, localeRoot) {
    const url = compareReviewSectionUrl(handle, localeRoot);
    if (!url) return null;
    if (reviewDataCache.has(url)) return reviewDataCache.get(url);

    const request = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((markup) => parseReviewDataFromSectionMarkup(markup))
      .catch(() => null);

    reviewDataCache.set(url, request);

    try {
      return await request;
    } catch {
      return null;
    }
  }

  async function enrichProductsWithReviewData(products, localeRoot) {
    const nextProducts = Array.isArray(products) ? products.slice() : [];
    const fallbackData = await Promise.all(
      nextProducts.map((product) => (product && !hasReviewData(product) ? fetchReviewData(product.handle, localeRoot) : null))
    );

    return nextProducts.map((product, index) => {
      const reviewData = fallbackData[index];
      if (!product || !reviewData) return product;

      return {
        ...product,
        metafields: {
          ...(product.metafields || {}),
          reviews: {
            ...((product.metafields && product.metafields.reviews) || {}),
            rating: {
              value: {
                rating: reviewData.rating,
                scale_max: reviewData.scaleMax,
              },
            },
            rating_count: reviewData.ratingCount,
          },
        },
        rating_count: reviewData.ratingCount ?? product.rating_count,
      };
    });
  }

  function productPageUrl(handle, localeRoot) {
    let base = (localeRoot || '').trim();
    if (base === '/' || base === '') {
      base = '';
    } else {
      base = base.replace(/\/+$/, '');
    }
    const path = `${base}/products/${encodeURIComponent(handle)}`;
    return path.replace(/\/{2,}/g, '/');
  }

  function getPrimaryVariant(product) {
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
    return product.variants.find((variant) => variant && variant.available) || product.variants[0] || null;
  }

  function canDirectAdd(product) {
    return Array.isArray(product?.variants) && product.variants.length === 1 && !!getPrimaryVariant(product)?.available;
  }

  function getCompareCtaLabel(root, product) {
    if (canDirectAdd(product)) {
      return root.getAttribute('data-td-label-add-to-cart') || 'Add to cart';
    }
    return root.getAttribute('data-td-label-choose-options') || 'Choose options';
  }

  function getCompareProductOptionsCount(product) {
    if (Array.isArray(product?.options)) return product.options.length;
    return 0;
  }

  function getCompareCtaMarkup(root, product) {
    const variant = getPrimaryVariant(product);
    const label = getCompareCtaLabel(root, product);
    const localeRoot = root.getAttribute('data-td-locale-root') || '';
    const url = productPageUrl(product.handle, localeRoot);
    const mode = canDirectAdd(product) ? 'add' : 'quick-add';
    const disabled = variant?.available === false || product?.available === false;

    return `<button
      type="button"
      class="button td-product-comparison__cta paragraph-small"
      data-td-compare-cta
      data-td-compare-cta-mode="${escapeAttr(mode)}"
      data-td-product-handle="${escapeAttr(product.handle || '')}"
      data-td-product-url="${escapeAttr(url)}"
      data-td-product-title="${escapeAttr(product?.title || '')}"
      data-td-product-options-count="${escapeAttr(String(getCompareProductOptionsCount(product)))}"
      ${variant?.id ? `data-td-variant-id="${escapeAttr(String(variant.id))}"` : ''}
      ${disabled ? 'disabled aria-disabled="true"' : ''}
    >${escapeHtml(label)}</button>`;
  }

  function getVendorQuickAddProxy() {
    let proxyCard = document.querySelector('[data-td-compare-quick-add-proxy]');
    let proxyQuickAdd = proxyCard?.querySelector('quick-add-component');
    let proxyLink = proxyCard?.querySelector('[ref="productCardLink"]');

    if (proxyCard instanceof HTMLElement && proxyQuickAdd instanceof HTMLElement && proxyLink instanceof HTMLAnchorElement) {
      return {
        proxyCard,
        proxyQuickAdd,
        proxyLink,
      };
    }

    proxyCard = document.createElement('product-card');
    proxyCard.setAttribute('data-td-compare-quick-add-proxy', '');
    proxyCard.setAttribute('aria-hidden', 'true');
    proxyCard.hidden = true;

    proxyLink = document.createElement('a');
    proxyLink.setAttribute('ref', 'productCardLink');

    proxyQuickAdd = document.createElement('quick-add-component');
    proxyQuickAdd.setAttribute('data-quick-add-button', 'choose');
    proxyQuickAdd.dataset.productTitle = '';
    proxyQuickAdd.dataset.productOptionsCount = '0';

    proxyCard.append(proxyLink, proxyQuickAdd);
    document.body.append(proxyCard);

    return {
      proxyCard,
      proxyQuickAdd,
      proxyLink,
    };
  }

  async function openCompareQuickAdd(root, button) {
    const productUrl = button.getAttribute('data-td-product-url');
    if (!productUrl) return;
    if (!customElements.get('quick-add-component') || !customElements.get('product-card')) {
      window.location.href = productUrl;
      return;
    }

    const { proxyQuickAdd, proxyLink } = getVendorQuickAddProxy();
    if (!(proxyQuickAdd instanceof HTMLElement) || typeof proxyQuickAdd.handleClick !== 'function') {
      window.location.href = productUrl;
      return;
    }

    proxyLink.href = productUrl;
    proxyQuickAdd.dataset.productTitle = button.getAttribute('data-td-product-title') || '';
    proxyQuickAdd.dataset.productOptionsCount = button.getAttribute('data-td-product-options-count') || '0';
    proxyQuickAdd.setAttribute('data-quick-add-button', 'choose');

    const originalText = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await proxyQuickAdd.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (typeof originalText === 'string') button.textContent = originalText;
    }
  }

  async function addCompareVariantToCart(root, button) {
    const variantId = button.getAttribute('data-td-variant-id');
    if (!variantId) return;

    const rootUrl = window.Shopify?.routes?.root || '/';
    const originalText = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const addResponse = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!addResponse.ok) {
        throw new Error(`Compare add to cart failed: ${addResponse.status}`);
      }

      const [cart, sections] = await Promise.all([
        fetch(`${rootUrl}cart.js`).then((response) => response.json()),
        fetch(`${rootUrl}?sections=cart-drawer,cart-icon-bubble`).then((response) => response.json()),
      ]);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'td-product-compare',
            data: {
              itemCount: cart?.item_count,
              sections,
              variantId,
            },
          },
        })
      );

      document.querySelector('cart-drawer-component')?.open?.();

      const itemAddedLabel = root.getAttribute('data-td-label-item-added');
      if (itemAddedLabel) {
        button.textContent = itemAddedLabel;
        window.setTimeout(() => {
          button.textContent = originalText;
        }, 1200);
      }
    } catch (error) {
      console.error(error);
      button.textContent = originalText;
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1200);
    }
  }

  function mergeHandlesFromStore(primaryHandle, shopUrl) {
    let handles = parseComparePageHandles(primaryHandle);
    if (handles.length >= 2) return handles;

    const fromStore = store.getItems().map((i) => i.handle).filter(Boolean);
    const merged = [];
    if (primaryHandle) merged.push(primaryHandle);
    for (const h of fromStore) {
      if (h && !merged.includes(h)) merged.push(h);
    }
    if (merged.length < 2) return handles.length ? handles : merged;

    const next = merged.slice(0, MAX_ITEMS);
    window.history.replaceState({}, '', buildComparePageUrl(shopUrl, next[0], next.slice(1)));
    return next;
  }

  function bindCompareFiltersPanel(root) {
    const panel = root.querySelector('[data-td-compare-filters-panel]');
    const backdrop = root.querySelector('[data-td-compare-filters-backdrop]');
    const toggle = root.querySelector('[data-td-compare-filters-toggle]');
    const closeBtn = root.querySelector('[data-td-compare-filters-close]');
    if (!panel || !toggle) return;

    const setOpen = (open) => {
      panel.setAttribute('data-td-filters-open', open ? 'true' : 'false');
      if (backdrop) {
        backdrop.setAttribute('data-td-visible', open ? 'true' : 'false');
        backdrop.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('td-product-comparison--filters-open', open);
      if (open) {
        if (!root._tdFilterEscBound) {
          root._tdFilterEscBound = (e) => {
            if (e.key === 'Escape' && panel.getAttribute('data-td-filters-open') === 'true') {
              setOpen(false);
            }
          };
          document.addEventListener('keydown', root._tdFilterEscBound);
        }
      } else if (root._tdFilterEscBound) {
        document.removeEventListener('keydown', root._tdFilterEscBound);
        root._tdFilterEscBound = null;
      }
    };

    toggle.addEventListener('click', () => {
      const open = panel.getAttribute('data-td-filters-open') !== 'true';
      setOpen(open);
    });
    closeBtn?.addEventListener('click', () => setOpen(false));
    backdrop?.addEventListener('click', () => setOpen(false));
  }

  const COMPARE_ROW_TYPES = new Set([
    'heading',
    'description',
    'vendor',
    'availability',
    'rating',
    'dynamic_comparison',
    'custom_liquid',
  ]);

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/"/g, '\\"');
  }

  function rowSelector(rowId) {
    return `[data-td-compare-row-id="${cssEscape(rowId)}"]`;
  }

  function parseCompareRowConfig(root) {
    const configScript = root.querySelector('[data-td-compare-row-config]');
    if (!configScript) return [];
    try {
      const parsed = JSON.parse(configScript.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((raw, index) => {
          const type = COMPARE_ROW_TYPES.has(raw?.type) ? raw.type : null;
          if (!type) return null;
          const fallbackId = `td-compare-row-${index + 1}`;
          return {
            id: raw?.id || fallbackId,
            type,
            label: raw?.label || '',
            heading: raw?.heading || '',
            title: raw?.title || '',
            text: raw?.text || '',
            customLiquid: raw?.custom_liquid || '',
          };
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  function getRowGroups(root) {
    if (Array.isArray(root._tdCompareRowGroups)) return root._tdCompareRowGroups;
    const rows = parseCompareRowConfig(root);
    const groups = [];
    let currentGroup = null;

    rows.forEach((row) => {
      if (row.type === 'heading') {
        currentGroup = {
          id: row.id,
          heading: row.heading || row.label || '',
          rows: [],
        };
        groups.push(currentGroup);
        return;
      }
      if (!currentGroup) {
        currentGroup = {
          id: 'td-compare-group-default',
          heading: '',
          rows: [],
        };
        groups.push(currentGroup);
      }
      currentGroup.rows.push(row);
    });

    root._tdCompareRowGroups = groups.filter((group) => Array.isArray(group.rows) && group.rows.length > 0);
    return root._tdCompareRowGroups;
  }

  function getFilterState(root) {
    const state = {};
    root.querySelectorAll('[data-td-compare-filter]').forEach((input) => {
      const rowId = input.getAttribute('data-td-compare-filter');
      if (rowId) state[rowId] = input.checked;
    });
    return state;
  }

  function syncFilterChips(root) {
    const wrap = root.querySelector('[data-td-compare-filter-chips]');
    const activeFilters = root.querySelector('[data-td-compare-active-filters]');
    const template = root.querySelector('[data-td-compare-chip-template]');
    if (!wrap || !(wrap instanceof HTMLElement)) return;
    const on = [...root.querySelectorAll('[data-td-compare-filter]')].filter((input) => input.checked);
    wrap.innerHTML = '';

    if (activeFilters instanceof HTMLElement) {
      activeFilters.hidden = on.length === 0;
    }

    on.forEach((input) => {
      const label = input.getAttribute('data-td-filter-label') || input.getAttribute('data-td-compare-filter');
      const rowId = input.getAttribute('data-td-compare-filter');
      if (!rowId) return;

      let chipItem = null;
      if (template instanceof HTMLTemplateElement) {
        chipItem = template.content.firstElementChild?.cloneNode(true) || null;
      }

      if (!(chipItem instanceof HTMLElement)) {
        chipItem = document.createElement('li');
        chipItem.className = 'td-product-comparison__chip-item';
        chipItem.innerHTML = `<button type="button" class="td-product-comparison__chip pills__pill pills__pill--desktop-small facets-remove__pill" data-td-compare-chip-button><span data-td-compare-chip-label></span></button>`;
      }

      const chipButton = chipItem.querySelector('[data-td-compare-chip-button]');
      const chipLabel = chipItem.querySelector('[data-td-compare-chip-label]');
      if (!(chipButton instanceof HTMLElement) || !(chipLabel instanceof HTMLElement)) return;
      chipButton.setAttribute('data-td-compare-filter-row-id', rowId);
      chipLabel.textContent = label || '';
      wrap.appendChild(chipItem);
    });
  }

  function renderFilterControls(root, groups) {
    const form = root.querySelector('[data-td-compare-filters-form]');
    if (!form) return;

    const currentState = root._tdCompareFilterState || getFilterState(root);
    const checkboxIcon = root.getAttribute('data-td-checkbox-icon') || '';
    form.innerHTML = '';

    groups.forEach((group) => {
      if (!group.rows.length) return;
      const groupWrap = document.createElement('div');
      groupWrap.className = 'td-product-comparison__filters-group';

      if (group.heading) {
        const heading = document.createElement('p');
        heading.className = 'td-product-comparison__filters-group-title paragraph-small';
        heading.textContent = group.heading;
        groupWrap.appendChild(heading);
      }

      const list = document.createElement('ul');
      list.className = 'td-product-comparison__filters-list';

      group.rows.forEach((row) => {
        const item = document.createElement('li');
        item.className = 'td-product-comparison__filter-item';

        const inputId = `${root.id || 'td-compare'}-filter-${row.id}`;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = inputId;
        input.className = 'checkbox__input td-product-comparison__filter-input';
        input.setAttribute('data-td-compare-filter', row.id);
        input.setAttribute('data-td-filter-label', row.label || row.title || row.id);
        input.checked = currentState[row.id] === true;

        const label = document.createElement('label');
        label.className = 'checkbox__label paragraph-small td-product-comparison__filter-label';
        label.setAttribute('for', inputId);
        label.innerHTML = `${checkboxIcon}<span class="checkbox__label-text">${escapeHtml(row.label || row.title || row.id)}</span>`;

        const checkbox = document.createElement('div');
        checkbox.className = 'checkbox td-product-comparison__filter-checkbox';
        checkbox.appendChild(input);
        checkbox.appendChild(label);

        item.appendChild(checkbox);
        list.appendChild(item);
      });

      groupWrap.appendChild(list);
      form.appendChild(groupWrap);
    });
  }

  function applyRowVisibility(root) {
    const selectedRows = new Set(
      [...root.querySelectorAll('[data-td-compare-filter]')]
        .filter((input) => input.checked)
        .map((input) => input.getAttribute('data-td-compare-filter'))
        .filter(Boolean)
    );
    const hasActiveFilters = selectedRows.size > 0;
    const visibleGroups = new Set();

    root.querySelectorAll('[data-td-compare-row-id]').forEach((row) => {
      const rowId = row.getAttribute('data-td-compare-row-id');
      const isVisible = !hasActiveFilters || selectedRows.has(rowId);
      row.hidden = !isVisible;

      if (isVisible) {
        const groupId = row.getAttribute('data-td-compare-group-id');
        if (groupId) visibleGroups.add(groupId);
      }
    });

    root.querySelectorAll('[data-td-compare-group-row-id]').forEach((row) => {
      const groupId = row.getAttribute('data-td-compare-group-row-id');
      row.hidden = !groupId || !visibleGroups.has(groupId);
    });

    root._tdCompareFilterState = getFilterState(root);
    syncFilterChips(root);
  }

  function renderRatingCell(product) {
    let rating = null;
    let scaleMax = 5;
    let count = null;
    const reviews = product?.metafields?.reviews;
    const ratingSource = reviews?.rating?.value || reviews?.rating;

    if (typeof ratingSource === 'object' && ratingSource !== null) {
      const candidate = Number(ratingSource.rating);
      if (Number.isFinite(candidate) && candidate > 0) rating = candidate;
      const maxCandidate = Number(ratingSource.scale_max);
      if (Number.isFinite(maxCandidate) && maxCandidate > 0) scaleMax = maxCandidate;
    } else if (Number.isFinite(Number(ratingSource)) && Number(ratingSource) > 0) {
      rating = Number(ratingSource);
    } else if (Number.isFinite(Number(product?.rating)) && Number(product.rating) > 0) {
      rating = Number(product.rating);
    }

    const reviewCount = Number(reviews?.rating_count || product?.rating_count);
    if (Number.isFinite(reviewCount) && reviewCount >= 0) count = reviewCount;

    if (!Number.isFinite(rating) || rating <= 0) {
      return '<span class="td-product-comparison__placeholder">&mdash;</span>';
    }

    const stars = Math.max(1, Math.min(5, Math.round(rating)));
    const starsText = '★'.repeat(stars);
    return `<div class="td-product-comparison__rating">
      <span class="td-product-comparison__rating-stars" aria-hidden="true">${escapeHtml(starsText)}</span>
      <span class="td-product-comparison__rating-value paragraph-small">${escapeHtml(rating.toFixed(1))} / ${escapeHtml(String(scaleMax))}</span>
      ${count == null ? '' : `<span class="td-product-comparison__rating-count paragraph-small">(${escapeHtml(String(count))})</span>`}
    </div>`;
  }

  function renderRowCell(row, product, L) {
    if (!row || !product) return '<span class="td-product-comparison__placeholder">&mdash;</span>';

    const label = row.label || row.title || row.id;
    let content = '';

      switch (row.type) {
      case 'description': {
        const text = stripHtml(product.description || product.body_html || '');
        content = text
          ? `<div class="td-product-comparison__value--description show-more__content-wrapper" data-td-compare-show-more-content data-show-more-state="closed">
              <div class="td-product-comparison__description-text" data-td-compare-show-more-body>${escapeHtml(text)}</div>
              <div class="show-more__toggle-more-wrapper">
                <button type="button" class="show-more__toggle-more paragraph" data-td-compare-show-more-toggle aria-expanded="false"></button>
              </div>
            </div>`
          : '<span class="td-product-comparison__placeholder">&mdash;</span>';
        break;
      }
      case 'vendor':
        content = product.vendor
          ? `<span>${escapeHtml(product.vendor)}</span>`
          : '<span class="td-product-comparison__placeholder">&mdash;</span>';
        break;
      case 'availability':
        content = product.available ? escapeHtml(L('in_stock')) : escapeHtml(L('out_of_stock'));
        break;
      case 'rating':
        content = renderRatingCell(product);
        break;
      case 'dynamic_comparison': {
        content = row.text
          ? `<div class="td-product-comparison__dynamic-text">${row.text}</div>`
          : '<span class="td-product-comparison__placeholder">&mdash;</span>';
        break;
      }
      case 'custom_liquid': {
        const text = stripHtml(row.customLiquid || '', 280);
        content = text
          ? `<div class="td-product-comparison__custom-liquid">${escapeHtml(text)}</div>`
          : '<span class="td-product-comparison__placeholder">&mdash;</span>';
        break;
      }
      default:
        content = '<span class="td-product-comparison__placeholder">&mdash;</span>';
    }

    return `<div class="td-product-comparison__cell">
      ${label ? `<h3 class="td-product-comparison__cell-label paragraph">${escapeHtml(label)}</h3>` : ''}
      ${content}
    </div>`;
  }

  function syncCompareShowMore(root) {
    const showMoreLabel = root.getAttribute('data-td-label-show-more') || 'Show more';

    window.requestAnimationFrame(() => {
      root.querySelectorAll('[data-td-compare-show-more-content]').forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement)) return;
        const button = wrapper.querySelector('[data-td-compare-show-more-toggle]');
        if (!(button instanceof HTMLButtonElement)) return;

        wrapper.dataset.showMoreState = 'closed';
        wrapper.removeAttribute('data-show-more-animation');
        wrapper.style.removeProperty('height');
        wrapper.classList.remove('show-more__content-wrapper--no-overflow');
        button.textContent = showMoreLabel;
        button.setAttribute('aria-expanded', 'false');

        if (wrapper.scrollHeight <= wrapper.clientHeight + 1) {
          wrapper.classList.add('show-more__content-wrapper--no-overflow');
        }
      });
    });
  }

  function toggleCompareShowMore(root, button) {
    if (!(button instanceof HTMLButtonElement)) return;
    const wrapper = button.closest('[data-td-compare-show-more-content]');
    if (!(wrapper instanceof HTMLElement) || wrapper.classList.contains('show-more__content-wrapper--no-overflow')) return;

    const showMoreLabel = root.getAttribute('data-td-label-show-more') || 'Show more';
    const showLessLabel = root.getAttribute('data-td-label-show-less') || 'Show less';
    const isOpen = wrapper.dataset.showMoreState === 'open';
    const startHeight = wrapper.getBoundingClientRect().height;

    wrapper.style.height = `${startHeight}px`;

    if (isOpen) {
      wrapper.dataset.showMoreState = 'closed';
      wrapper.dataset.showMoreAnimation = 'open=>closed';
      button.textContent = showMoreLabel;
      button.setAttribute('aria-expanded', 'false');
    } else {
      wrapper.dataset.showMoreState = 'open';
      wrapper.dataset.showMoreAnimation = 'closed=>open';
      button.textContent = showLessLabel;
      button.setAttribute('aria-expanded', 'true');
    }

    wrapper.style.height = 'auto';
    const endHeight = wrapper.getBoundingClientRect().height;
    wrapper.style.height = `${startHeight}px`;

    window.requestAnimationFrame(() => {
      wrapper.style.height = `${endHeight}px`;
    });
  }

  function syncCompareStickyLayout(root) {
    if (!(root instanceof HTMLElement)) return;

    const sticky = root.querySelector('[data-td-compare-sticky]');
    const stickyInner = sticky?.querySelector('.td-product-comparison__sticky-inner');
    const tableWrap = root.querySelector('.td-product-comparison__table-wrap');
    const table = root.querySelector('.td-product-comparison__table');

    if (!(sticky instanceof HTMLElement) || !(stickyInner instanceof HTMLElement)) return;

    if (window.innerWidth >= COMPARE_DESKTOP_BREAKPOINT) {
      sticky.style.removeProperty('max-width');
      stickyInner.style.removeProperty('width');
      stickyInner.style.removeProperty('transform');
      return;
    }

    if (!(tableWrap instanceof HTMLElement) || !(table instanceof HTMLElement)) return;

    sticky.style.maxWidth = `${tableWrap.clientWidth}px`;
    stickyInner.style.width = `${table.scrollWidth}px`;
    stickyInner.style.transform = `translateX(${-tableWrap.scrollLeft}px)`;
  }

  function renderComparePage(root, products, shopUrl) {
    const table = root.querySelector('.td-product-comparison__table');
    const thead = root.querySelector('[data-td-compare-thead]');
    const tbody = root.querySelector('[data-td-compare-tbody]');
    const stickyInner = root.querySelector('[data-td-compare-sticky-inner]');
    if (!table || !thead || !tbody) return;

    const L = (key) =>
      root.getAttribute(`data-td-label-${String(key).replace(/_/g, '-')}`) || key;
    const removeLabel = escapeAttr(root.getAttribute('data-td-remove-label') || 'Remove');
    const groups = getRowGroups(root);

    renderFilterControls(root, groups);
    table.querySelector('colgroup')?.remove();
    table.insertAdjacentHTML(
      'afterbegin',
      `<colgroup>${products
        .map(() => '<col class="td-product-comparison__col-product">')
        .join('')}</colgroup>`
    );

    const headCells = products
      .map((product) => {
        const img = productImageUrl(product);
        const imgTag = img
          ? `<img src="${escapeAttr(img)}" alt="" width="160" height="160" loading="lazy" class="td-product-comparison__head-img">`
          : '<span class="td-product-comparison__head-placeholder"></span>';
        const productUrl = productPageUrl(product.handle, root.getAttribute('data-td-locale-root') || '');
        return `<th scope="col" class="td-product-comparison__col-head">
          <div class="td-product-comparison__head-card">
            ${renderCompareBadge(root, product)}
            <div class="td-product-comparison__head-media">${imgTag}</div>
            <div class="td-product-comparison__head-meta">
              <p class="td-product-comparison__head-title paragraph-small">
                <a href="${escapeAttr(productUrl)}">${escapeHtml(product.title || product.handle || '')}</a>
              </p>
              ${renderComparePrice(product)}
            </div>
            ${getCompareCtaMarkup(root, product)}
            <button type="button" class="td-product-comparison__col-remove button button-unstyled" data-td-compare-page-remove data-td-handle="${escapeAttr(product.handle)}" aria-label="${removeLabel}">
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </th>`;
      })
      .join('');

    thead.innerHTML = `<tr class="td-product-comparison__thead-row">${headCells}</tr>`;

    const rows = [];
    groups.forEach((group) => {
      if (group.heading) {
        rows.push(
          `<tr class="td-product-comparison__row-heading" data-td-compare-ignore-filter data-td-compare-group-row-id="${escapeAttr(group.id)}"><th class="td-product-comparison__group-heading" scope="colgroup" colspan="${products.length}"><h2 class="h4">${escapeHtml(group.heading)}</h2></th></tr>`
        );
      }

      group.rows.forEach((row, rowIndex) => {
        const rowClass = rowIndex === group.rows.length - 1 ? 'td-product-comparison__row-last' : '';
        const cells = products
          .map((product) => `<td>${renderRowCell(row, product, L)}</td>`)
          .join('');
        rows.push(
          `<tr class="td-product-comparison__row ${rowClass}" data-td-compare-row-id="${escapeAttr(row.id)}" data-td-compare-group-id="${escapeAttr(group.id)}">${cells}</tr>`
        );
      });
    });

    tbody.innerHTML = rows.join('');

    if (stickyInner) {
      stickyInner.innerHTML = `
        <div class="td-product-comparison__sticky-products-track" style="--td-compare-sticky-columns:${products.length}">
          ${products
            .map((product) => {
              return `<div class="td-product-comparison__sticky-item">
                <div class="td-product-comparison__sticky-item-content">
                  <p class="td-product-comparison__sticky-item-title paragraph-small">${escapeHtml(product.title || '')}</p>
                  ${renderComparePrice(product)}
                </div>
                ${getCompareCtaMarkup(root, product)}
                <button type="button" class="td-product-comparison__sticky-remove button button-unstyled" data-td-compare-page-remove data-td-handle="${escapeAttr(product.handle)}" aria-label="${removeLabel}">
                  <span aria-hidden="true">×</span>
                </button>
              </div>`;
            })
            .join('')}
        </div>
      `;
    }

    syncCompareShowMore(root);
    applyRowVisibility(root);
    syncCompareStickyLayout(root);
  }

  function removeCompareColumn(root, handle) {
    let products = root._tdCompareProducts || [];
    products = products.filter((p) => p.handle !== handle);
    const shopUrl = root.getAttribute('data-td-shop-url') || '';
    if (products.length < 2) {
      store.clear();
      window.location.href = routesRootFromDom();
      return;
    }
    root._tdCompareProducts = products;
    const newPrimary = products[0].handle;
    const others = products.slice(1).map((p) => p.handle);
    window.history.replaceState({}, '', buildComparePageUrl(shopUrl, newPrimary, others));
    renderComparePage(root, products, shopUrl);
    store.setItems(productsToStoreItems(products, shopUrl));
    syncCardRoots(store.getItems());
  }

  async function comparePageInit(root) {
    const primary = root.getAttribute('data-td-primary-handle');
    const shopUrl = root.getAttribute('data-td-shop-url') || '';
    if (!primary) return;
    if (root._tdCompareInitDone) return;

    const loading = root.querySelector('[data-td-compare-loading]');
    const empty = root.querySelector('[data-td-compare-empty]');
    const main = root.querySelector('[data-td-compare-main]');

    const localeRoot = root.getAttribute('data-td-locale-root') || '';
    let handles = mergeHandlesFromStore(primary, shopUrl);
    let productsRaw = await fetchProductsJson(handles, localeRoot);
    let products = productsRaw.filter(Boolean);
    products = await enrichProductsWithReviewData(products, localeRoot);

    if (loading) loading.hidden = true;

    if (products.length < 2) {
      if (empty) empty.hidden = false;
      if (main) main.hidden = true;
      root._tdCompareInitDone = true;
      return;
    }

    root._tdCompareInitDone = true;

    if (empty) empty.hidden = true;
    if (main) main.hidden = false;

    root._tdCompareProducts = products;
    renderComparePage(root, products, shopUrl);
    store.setItems(productsToStoreItems(products, shopUrl));
    syncCardRoots(store.getItems());

    if (!root._tdFiltersPanelBound) {
      root._tdFiltersPanelBound = true;
      bindCompareFiltersPanel(root);
    }

    if (!root._tdCompareUiBound) {
      root._tdCompareUiBound = true;
      root.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-td-compare-chip-button]');
        if (chip && root.contains(chip)) {
          e.preventDefault();
          const rowId = chip.getAttribute('data-td-compare-filter-row-id');
          const input = rowId ? root.querySelector(`[data-td-compare-filter="${cssEscape(rowId)}"]`) : null;
          if (input) {
            input.checked = false;
            applyRowVisibility(root);
          }
          return;
        }

        const cta = e.target.closest('[data-td-compare-cta]');
        if (cta && root.contains(cta) && !cta.disabled) {
          e.preventDefault();
          const mode = cta.getAttribute('data-td-compare-cta-mode');
          if (mode === 'add') {
            addCompareVariantToCart(root, cta).catch((error) => console.error(error));
          } else {
            openCompareQuickAdd(root, cta).catch((error) => console.error(error));
          }
          return;
        }

        const showMoreToggle = e.target.closest('[data-td-compare-show-more-toggle]');
        if (showMoreToggle && root.contains(showMoreToggle)) {
          e.preventDefault();
          toggleCompareShowMore(root, showMoreToggle);
          return;
        }

        const btn = e.target.closest('[data-td-compare-page-remove]');
        if (!btn || !root.contains(btn)) return;
        e.preventDefault();
        const h = btn.getAttribute('data-td-handle');
        if (h) removeCompareColumn(root, h);
      });
      root.querySelectorAll('[data-td-compare-page-clear]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          store.clear();
          window.location.href = routesRootFromDom();
        });
      });
      root.addEventListener('change', (e) => {
        const input = e.target.closest('[data-td-compare-filter]');
        if (!input || !root.contains(input)) return;
        applyRowVisibility(root);
      });
      root.querySelector('[data-td-compare-filters-clear]')?.addEventListener('click', (e) => {
        e.preventDefault();
        root.querySelectorAll('[data-td-compare-filter]').forEach((input) => {
          input.checked = false;
        });
        applyRowVisibility(root);
      });
      root.querySelector('[data-td-compare-filters-clear-top]')?.addEventListener('click', (e) => {
        e.preventDefault();
        root.querySelectorAll('[data-td-compare-filter]').forEach((input) => {
          input.checked = false;
        });
        applyRowVisibility(root);
      });
      root.addEventListener('transitionend', (e) => {
        const wrapper = e.target;
        if (!(wrapper instanceof HTMLElement) || !wrapper.matches('[data-td-compare-show-more-content]')) return;
        if (e.propertyName !== 'height') return;
        wrapper.style.removeProperty('height');
        wrapper.removeAttribute('data-show-more-animation');
      });
    }
  }

  class TDProductComparisonPage extends HTMLElement {
    connectedCallback() {
      const sticky = this.querySelector('[data-td-compare-sticky]');
      const sentinel = this.querySelector('[data-td-compare-sticky-sentinel]');
      if (sticky && sentinel && 'IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          ([entry]) => {
            sticky.classList.toggle('td-product-comparison__sticky--visible', !entry.isIntersecting);
          },
          { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
        );
        this._io.observe(sentinel);
      }
      comparePageInit(this).then(() => {
        if (!this._tdCompareScrollBound) {
          this._tdCompareScrollBound = true;
          this._tdCompareTableWrap = this.querySelector('.td-product-comparison__table-wrap');
          this._tdCompareSyncStickyLayout = () => syncCompareStickyLayout(this);
          this._tdCompareTableWrap?.addEventListener('scroll', this._tdCompareSyncStickyLayout);
          window.addEventListener('resize', this._tdCompareSyncStickyLayout);
        }
        syncCompareStickyLayout(this);
      }).catch(() => {});
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._tdCompareTableWrap?.removeEventListener('scroll', this._tdCompareSyncStickyLayout);
      window.removeEventListener('resize', this._tdCompareSyncStickyLayout);
      this._tdCompareTableWrap = null;
      this._tdCompareSyncStickyLayout = null;
      this._tdCompareScrollBound = false;
      if (this._tdFilterEscBound) {
        document.removeEventListener('keydown', this._tdFilterEscBound);
        this._tdFilterEscBound = null;
      }
      document.body.classList.remove('td-product-comparison--filters-open');
    }
  }

  if (!customElements.get('td-product-comparison-page')) {
    customElements.define('td-product-comparison-page', TDProductComparisonPage);
  }
})();
